"use client";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import useSlotProps from "@mui/utils/useSlotProps";
import * as React from "react";
import { Link as ToolpadLink } from "../Common/Link";
import { useActivePage } from "../useActivePage";
import { getItemTitle } from "../useNavigation/navigation";
import PageHeaderToolbar, { PageHeaderToolbarProps } from "./PageHeaderToolbar";

export interface Breadcrumb {
  /**
   * The title of the breadcrumb segment.
   */
  title: string;
  /**
   * The path the breadcrumb links to.
   */
  path?: string;
}
export interface PageContainerSlotProps {
  header: PageHeaderProps;
}

const PageContentHeader = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  gap: theme.spacing(2),
}));

export interface PageHeaderSlotProps {
  toolbar: PageHeaderToolbarProps;
}

export interface PageHeaderSlots {
  /**
   * The component that renders the actions toolbar.
   * @default PageHeaderToolbar
   */
  toolbar: React.ElementType;
}

export interface PageHeaderProps {
  /**
   * The title of the page. Leave blank to use the active page title.
   */
  title?: string;
  /**
   * The subheader of the page. Leave blank to use the active page subheader.
   */
  subheader?: string;
  /**
   * The breadcrumbs of the page. Leave blank to use the active page breadcrumbs.
   */
  breadcrumbs?: Breadcrumb[] | null;
  /**
   * The components used for each slot inside.
   */
  slots?: PageHeaderSlots;
  /**
   * The props used for each slot inside.
   */
  slotProps?: PageHeaderSlotProps;
}

function PageHeader(props: PageHeaderProps) {
  const { breadcrumbs, title, subheader } = props;

  const activePage = useActivePage();

  const resolvedBreadcrumbs =
    breadcrumbs !== null
      ? (breadcrumbs ?? activePage?.breadcrumbs ?? [])
      : null;
  const resolvedTitle = title ?? activePage?.title ?? "";
  const ToolbarComponent = props?.slots?.toolbar ?? PageHeaderToolbar;
  const toolbarSlotProps = useSlotProps({
    elementType: ToolbarComponent,
    ownerState: props,
    externalSlotProps: props?.slotProps?.toolbar,
    additionalProps: {},
  });

  return (
    <Stack>
      <Breadcrumbs aria-label="breadcrumb">
        {resolvedBreadcrumbs
          ? resolvedBreadcrumbs.map((item, index) => {
              return item.path ? (
                <Link
                  key={index}
                  component={ToolpadLink}
                  underline="hover"
                  color="inherit"
                  href={item.path}
                >
                  {getItemTitle(item)}
                </Link>
              ) : (
                <Typography key={index} color="text.primary">
                  {getItemTitle(item)}
                </Typography>
              );
            })
          : null}
      </Breadcrumbs>

      <PageContentHeader>
        <Stack justifyContent="start">
          {resolvedTitle ? (
            <Typography variant="h5" fontWeight="bold">
              {resolvedTitle}
            </Typography>
          ) : null}
          {subheader ? (
            <Typography variant="body2">{subheader}</Typography>
          ) : null}
        </Stack>
        <ToolbarComponent {...toolbarSlotProps} />
      </PageContentHeader>
    </Stack>
  );
}
export default PageHeader;
